import React from "react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, crumbLd } from "@/components/Breadcrumbs";

const DOCS = {
  privacy: {
    title: "Privacy Policy | Dotlands Realty",
    h1: "Privacy Policy",
    intro: "This Privacy Policy explains how Dotlands Realty collects, uses and protects the information you share when you use our website.",
    sections: [
      { h2: "Information we collect", body: "When you post a property, save listings or make an enquiry, we may collect your name, mobile number, email and the property details you submit. Basic usage data such as pages viewed may also be collected to improve the site." },
      { h2: "How we use it", body: "We use your information to verify and publish listings, connect buyers with our desk, respond to enquiries, process payments and improve our service. We do not sell your personal data to third parties." },
      { h2: "Sharing", body: "Contact details may be shared with our team to service your enquiry. Premium features may allow direct contact between users. Payment processing is handled securely by Razorpay." },
      { h2: "Your choices", body: "You can request access to, correction of, or deletion of your account data by contacting us. Continued use of the site indicates acceptance of this policy." },
    ],
  },
  terms: {
    title: "Terms & Conditions | Dotlands Realty",
    h1: "Terms & Conditions",
    intro: "By accessing and using the Dotlands Realty website you agree to the following terms.",
    sections: [
      { h2: "Use of the platform", body: "Dotlands Realty is a marketplace that connects buyers and sellers of land, plots and property in Telangana. You agree to provide accurate information and to use the platform lawfully." },
      { h2: "Listings", body: "Sellers are responsible for the accuracy of the information they post. Listings are published only after review and may be edited, hidden or removed at our discretion." },
      { h2: "No warranty", body: "We facilitate connections but are not a party to any transaction between users. We do not guarantee the sale, title, approval status or value of any property. Buyers must carry out their own due diligence." },
      { h2: "Payments", body: "Fees for promoted listings and subscriptions are shown before payment and are processed securely. Paid promotions are non-refundable once activated unless required by law." },
    ],
  },
  "listing-policy": {
    title: "Listing Policy | Dotlands Realty",
    h1: "Listing Policy",
    intro: "To keep the marketplace trustworthy, all listings follow this policy.",
    sections: [
      { h2: "Verification", body: "Every new listing is set to Pending Verification and is reviewed by our team before it appears publicly. A listing may be marked Dotlands Verified after review." },
      { h2: "Accuracy", body: "Prices, extent, survey numbers, facing and approval details must be accurate. We never automatically claim that a property is RERA, HMDA or DTCP approved — such claims are reviewed by an administrator." },
      { h2: "Prohibited content", body: "Duplicate, misleading, fraudulent or unlawful listings are not allowed and will be rejected or removed. Repeated violations may result in account suspension." },
      { h2: "Removal", body: "We may reject, hide or remove any listing that does not meet this policy, with a reason provided to the poster where possible." },
    ],
  },
  disclaimer: {
    title: "Disclaimer | Dotlands Realty",
    h1: "Disclaimer",
    intro: "Please read this disclaimer carefully before relying on any information on this website.",
    sections: [
      { h2: "Information only", body: "All property information is provided by sellers and is for general information only. While we review listings, we do not independently guarantee the accuracy, completeness or legal status of any listing." },
      { h2: "Due diligence", body: "Buyers should independently verify title, ownership, survey records, approvals and all legal documents, and consult qualified professionals before any transaction." },
      { h2: "No investment advice", body: "Content and guides on this website are educational and do not constitute financial or legal advice, and we make no guarantee of returns or appreciation." },
    ],
  },
};

export default function Legal({ doc }) {
  const d = DOCS[doc] || DOCS.privacy;
  const crumbs = [{ name: "Home", to: "/" }, { name: d.h1 }];
  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-10" data-testid={`legal-${doc}`}>
      <Seo title={d.title} description={d.intro} path={`/${doc}`} jsonLd={crumbLd(crumbs)} />
      <Breadcrumbs items={crumbs} />
      <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-[#0E3B2E]">{d.h1}</h1>
      <p className="text-slate-600 mt-3 leading-relaxed">{d.intro}</p>
      <div className="mt-8 space-y-8">
        {d.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-heading font-bold text-xl text-[#0E3B2E]">{s.h2}</h2>
            <p className="text-slate-700 mt-2 leading-relaxed">{s.body}</p>
          </section>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-10">Last updated 2026. For questions, please contact Dotlands Realty.</p>
    </div>
  );
}
